import { Checkbox, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import StatesHelper from '../../helpers/states_helper';
import ListItemText from '@mui/material/ListItemText';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Actions from '../../actions/actions';
import useValChecker from '../hooks/use_val_checker';
import NumberFormatInput, { numbersOnly } from '../shared/number_format_input';
import DisplayHelper from '../../helpers/display_helper';
import Footnote from './footnote';
import Footnotes from './footnotes';
import { ReactComponent as DarkSun } from '../../images/dark-sun.svg';
import { DebounceInput } from 'react-debounce-input';
import withDataLoaded from '../with_data_loaded';
import { userLicenseListLoader } from '../../actions/data_loaders';
import PageSpinner from '../shared/page_spinner';
import { useSelector } from 'react-redux';
import NoStateLicense from '../shared/no_state_license';
import cx from 'classnames';
import BeamButton from '../beam/beam_button';

const numFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 });

const defaultCol = { flex: 10, headerAlign: 'right', align: 'right', minWidth: 100 };
const numCol = { ...defaultCol, type: 'number' };
const percentCol = { ...numCol, valueFormatter: ({ value }) => numFormatter.format(value) + '%' };

const creditFilterOptions = { excellent: 'Excellent', very_good: 'Very Good', good: 'Good' };

const minAmountFinanced = 10001;

const genericFieldValidator = (field) => (v) => v === '' ? `${field} required` : null;
const amountFinancedValidator = (v) =>
  genericFieldValidator('Amount')(v) || (v >= minAmountFinanced ? '' : 'Amount must be at least 10,001');

const NoRows = ({ text, isProcessing }) =>
  isProcessing ? (
    <div className={'load-spin spin'}>
      <DarkSun name="sun" className="dark-sun-icon icon" />
    </div>
  ) : (
    <Typography id="no-rows-found" className="no-rows-found">
      {text}
    </Typography>
  );

const DollarFormatInput = React.forwardRef((props, ref) => NumberFormatInput({ ...props, inputRef: ref }, 0, false));

const mapUserFMs = (userFMs) =>
  userFMs.map((fm, i) => ({
    id: i,
    groupName: fm.calculationModel.groupName,
    term: fm.term,
    apr: fm.apr,
    introPayment: fm.calculations.preReAmMonthlyRounded,
    monthlyPayment: fm.calculations.postReAmMonthlyWithPrePaymentRounded,
    defaultSelectionPriority: fm.calculationModel.defaultSelectionPriority,
    requiredCreditRating: fm.calculationModel.requiredCreditRating,
    isRBP: !!fm.calculationModel.requiredCreditRating,
    productSku: fm.productSku,
  }));

const ProductComparisonPage = () => {
  const licensedStates = useSelector((redux) => redux.userLicenseList);
  const userProducts = useSelector((redux) => redux.user?.products);

  const [userFinancingMethods, setUserFinancingMethods] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [termFilters, setTermFilters] = useState([]);
  const [termFilterOptions, setTermFilterOptions] = useState([]);
  const [aprFilters, setAprFilters] = useState([]);
  const [aprFilterOptions, setAprFilterOptions] = useState([]);
  const [groupNameFilters, setGroupNameFilters] = useState([]);
  const [groupNameFilterOptions, setGroupNameFilterOptions] = useState([]);
  const [productSkuFilter, setProductSkuFilter] = useState('SLOAN_MEDALLION_001');
  const productFilterOptions = useMemo(
    () => userProducts.filter((p) => !p.productSku.toLowerCase().startsWith('cash_')),
    [userProducts]
  );
  const [creditFilter, setCreditFilter] = useState('');

  const [cstate, setCstate, cstateError] = useValChecker(
    licensedStates?.length === 1 ? licensedStates[0]?.state : null,
    genericFieldValidator('State')
  );
  const [amountFinanced, setAmountFinanced, amountFinancedError] = useValChecker(null, amountFinancedValidator);
  const [prePayPercent, setPrePayPercent, prePayPercentError] = useValChecker(30, genericFieldValidator('Prepay %'));
  const [prePayAmount, setPrePayAmount, prePaymentError] = useValChecker(null, genericFieldValidator('Prepay $'));

  const anyErrors = cstateError || amountFinancedError || prePayPercentError || prePaymentError;
  const necessaryFieldsFilled = cstate && amountFinanced && prePayPercent != null && prePayPercent !== '' && !anyErrors;
  const hasRBPProducts = useMemo(() => userFinancingMethods.some((fm) => fm.isRBP), [userFinancingMethods]);
  const isHVAC = useMemo(() => productSkuFilter.toLowerCase().startsWith('hvac_'), [productSkuFilter]);

  const updateFilterOptions = useCallback(
    (forceFilterMatch) => {
      const allYears = [...new Set(userFinancingMethods?.map((fm) => fm.term) ?? [])];
      const allAprs = [
        ...new Set(
          userFinancingMethods
            ?.filter((fm) => !fm.isRBP || fm.requiredCreditRating === creditFilter)
            ?.map((fm) => fm.apr) ?? []
        ),
      ];
      const allGroupNames = [...new Set(userFinancingMethods?.map((fm) => fm.groupName) ?? [])];

      setTermFilterOptions(allYears);
      setAprFilterOptions(allAprs);
      setGroupNameFilterOptions(allGroupNames);

      if (termFilters.length === 0) setTermFilters(allYears);
      if (aprFilters.length === 0 || forceFilterMatch) setAprFilters(allAprs);
      if (groupNameFilters.length === 0) setGroupNameFilters(allGroupNames);
      if (!creditFilter && hasRBPProducts) setCreditFilter('very_good');
    },
    [userFinancingMethods, termFilters, aprFilters, groupNameFilters, creditFilter, hasRBPProducts]
  );

  const fetchUserFMs = useCallback(async () => {
    setIsProcessing(true);
    const fms = await Actions.fetchUserFinancingMethods({ amountFinanced, cstate, prePaymentPercent: prePayPercent });
    setIsProcessing(false);
    if (fms) {
      const newUserFinancingMethods = mapUserFMs(fms);
      setUserFinancingMethods(newUserFinancingMethods);
    }
    return !!fms;
  }, [amountFinanced, cstate, prePayPercent]);

  /* eslint-disable  react-hooks/exhaustive-deps */
  useEffect(() => {
    if (
      isCalculating &&
      amountFinanced != null &&
      amountFinanced >= minAmountFinanced &&
      prePayPercent != null &&
      prePayAmount != null
    ) {
      fetchUserFMs();
    }
  }, [isCalculating, cstate, prePayAmount, amountFinanced]);

  useEffect(() => updateFilterOptions(false), [userFinancingMethods]);
  useEffect(() => updateFilterOptions(true), [creditFilter]);
  /* eslint-enable  react-hooks/exhaustive-deps */

  const updatePrePaymentValues = (amountFinanced, { newAmount, newPercent }) => {
    let updatedAmount, updatedPercent;
    if (newPercent != null) {
      if (amountFinanced) {
        updatedAmount = Math.round(amountFinanced * (newPercent / 100));
      }
      updatedPercent = newPercent;
    } else if (newAmount != null) {
      updatedAmount = newAmount;
      if (amountFinanced) {
        updatedPercent = Math.round((100 * newAmount) / amountFinanced);
      }
    }

    //must update percent first
    if (updatedPercent != null) setPrePayPercent(updatedPercent);
    if (updatedAmount != null) setPrePayAmount(updatedAmount);
  };

  const triggerCalculations = async () => {
    setCstate(cstate ?? '');
    setAmountFinanced(amountFinanced ?? '');
    setPrePayAmount(prePayAmount ?? '');

    if (necessaryFieldsFilled) {
      if (await fetchUserFMs()) {
        setIsCalculating(true);
      }
    }
  };

  const aprFootnote = useMemo(
    () =>
      !isCalculating ? (
        ''
      ) : (
        <Footnote
          id="aprFootnote"
          content={`APR and payment ${
            hasRBPProducts ? 'are based on the applicant(s) credit quality, and ' : ''
          }assumes monthly payments are made through recurring ACH. If you choose to make payments via check or one time electronic payment, your monthly payment will be higher, as your annual percentage rate will not be eligible for a 0.50% discount.`}
          isBold={true}
        />
      ),
    [hasRBPProducts, isCalculating]
  );

  const monthlyPaymentFootnote = useMemo(
    () =>
      !isCalculating || isHVAC ? (
        ''
      ) : (
        <Footnote
          id="monthlyPaymentFootnote"
          content="This financing product offers reduced monthly payments for the first 18 months, with the intent to encourage prepayment before a re-amortization of the loan at the end of month 18. Prepaying during this time will reduce your monthly payments after that re-amortization for the remainder of the loan. "
          isBold={true}
        />
      ),
    [isCalculating, isHVAC]
  );

  const filteredRows = userFinancingMethods.filter(
    (fm) =>
      termFilters.includes(fm.term) &&
      aprFilters.includes(fm.apr) &&
      fm.productSku === productSkuFilter &&
      (!fm.isRBP || creditFilter === fm.requiredCreditRating) &&
      (!fm.isRBP || groupNameFilters.includes(fm.groupName))
  );

  const hRender = (captureText, extraText) => () =>
    (
      <span className="header-text-container">
        <span className="header-text">{captureText}</span>
        {extraText}
      </span>
    );

  const columns = [
    { field: 'id', hide: true },
    hasRBPProducts && { ...defaultCol, flex: 7, field: 'groupName', renderHeader: hRender('Group Name') },
    { ...numCol, flex: 5, field: 'term', renderHeader: hRender('Term') },
    { ...percentCol, field: 'apr', renderHeader: hRender('APR', aprFootnote) },
    !isHVAC && {
      ...defaultCol,
      field: 'introPayment',
      renderHeader: hRender('Intro Payment', monthlyPaymentFootnote),
      renderCell: ({ value }) => (
        <div className={cx('padded-cell', { 'loading-blur': isProcessing })}>
          {DisplayHelper.formatMoneyComma(value)}
        </div>
      ),
    },
    {
      ...defaultCol,
      field: 'monthlyPayment',
      renderHeader: hRender('Monthly Payment', monthlyPaymentFootnote),
      renderCell: ({ value }) => (
        <span className={cx('blue-bold', 'padded-cell', { 'loading-blur': isProcessing })}>
          {DisplayHelper.formatMoneyComma(value)}
        </span>
      ),
    },
  ].filter(Boolean);

  if (licensedStates?.length === 0) {
    return (
      <div className="product-comparison-page-no-licenses">
        <NoStateLicense plural />
      </div>
    );
  }

  return (
    <div className="product-comparison-page">
      <h1 className="page-header">Monthly Payment Calculator</h1>
      <div className="search-input-container">
        <TextField
          id="product-input-field"
          className="product-type-input"
          size="small"
          margin="dense"
          label="Project Type"
          value={productSkuFilter}
          // SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          select
          variant="outlined"
          onChange={(e) => setProductSkuFilter(e.target.value)}
        >
          {productFilterOptions.map((p) => {
            const productName = p.name.replaceAll('Concert ', '');
            return (
              <MenuItem
                k={p.productSku}
                value={p.productSku}
                id={`qa-option-${productName?.toLowerCase()?.replaceAll(/[^a-z]/g, '')}`}
              >
                {productName}
              </MenuItem>
            );
          })}
        </TextField>
        <TextField
          id="state-input-field"
          select
          size="small"
          margin="dense"
          className="state-input"
          label={cstate == null ? 'Select a State' : 'State'}
          value={cstate ?? ''}
          onChange={(e) => setCstate(e.target.value)}
          error={!!cstateError}
          helperText={cstateError}
        >
          {StatesHelper.validStates()
            .filter((stateObj) => licensedStates.some(({ state }) => state === stateObj.abbreviation))
            .map((stateObj, i) => (
              <MenuItem key={i} value={stateObj.abbreviation} id={`qa-option-${stateObj.name.replaceAll(' ', '')}`}>
                {stateObj.name}
              </MenuItem>
            ))}
        </TextField>
        <DebounceInput
          element={TextField}
          id="amount-financed-input-field"
          className="amount-financed-input"
          size="small"
          margin="dense"
          label="Enter Loan Amount"
          InputProps={{ inputComponent: DollarFormatInput }}
          value={amountFinanced ?? ''}
          onChange={(e) => {
            const newAmountFinanced = numbersOnly(e?.target);
            setAmountFinanced(newAmountFinanced);
            updatePrePaymentValues(newAmountFinanced, { newPercent: prePayPercent });
          }}
          error={!!amountFinancedError}
          helperText={amountFinancedError}
          debounceTimeout={300}
        />
        {!isHVAC && (
          <div className="prepay-input-container">
            <DebounceInput
              element={TextField}
              id="prepay-percent-input-field"
              className="prepay-input prepay-percent"
              size="small"
              margin="dense"
              label="Prepay %"
              InputProps={{ endAdornment: [null, ''].includes(prePayPercent) ? '' : '%' }}
              value={prePayPercent ?? ''}
              onChange={(e) => updatePrePaymentValues(amountFinanced, { newPercent: numbersOnly(e?.target) })}
              error={!!prePayPercentError}
              helperText={prePayPercentError}
              debounceTimeout={300}
            />
            <div className="prepay-link-icon">&harr;</div>
            <DebounceInput
              element={TextField}
              id="prepay-amount-input-field"
              className="prepay-input prepay-dollar"
              size="small"
              margin="dense"
              label="Prepay $"
              InputLabelProps={{ shrink: true }}
              InputProps={{ inputComponent: DollarFormatInput }}
              value={prePayAmount ?? ''}
              onChange={(e) => updatePrePaymentValues(amountFinanced, { newAmount: numbersOnly(e?.target) })}
              error={!!prePaymentError}
              helperText={prePaymentError}
              debounceTimeout={300}
            />
          </div>
        )}
      </div>
      <div className="filter-container">
        <span className="filters-label">Filters:</span>
        <div className="filter-input-container">
          <FormControl
            id="year-filter-form"
            size="small"
            margin="normal"
            disabled={!termFilterOptions.length}
            focused={isCalculating}
          >
            <InputLabel>Term</InputLabel>
            <Select
              inputProps={{ id: 'year-filter-input-select' }}
              size="small"
              label="Year"
              multiple
              placeholder="Year"
              variant="outlined"
              color="primary"
              className="filter-select-input"
              value={termFilters}
              onChange={({ target: { value } }) => setTermFilters(value)}
              renderValue={(selected) => (selected.length === termFilterOptions.length ? 'All' : selected.join(', '))}
            >
              {termFilterOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox color="primary" checked={termFilters.includes(opt)} id={`filter-option-${opt}`} />
                  <ListItemText primary={opt} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            id="apr-filter-form"
            size="small"
            margin="normal"
            disabled={!aprFilterOptions.length}
            focused={isCalculating}
          >
            <InputLabel>APR</InputLabel>
            <Select
              inputProps={{ id: 'apr-filter-input-select' }}
              multiple
              label="APR"
              placeholder="APR"
              variant="outlined"
              color="primary"
              className="filter-select-input"
              value={aprFilters}
              onChange={({ target: { value } }) => setAprFilters(value)}
              renderValue={(selected) => (selected.length === aprFilterOptions.length ? 'All' : selected.join(', '))}
            >
              {aprFilterOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox
                    color="primary"
                    checked={aprFilters.includes(opt)}
                    id={`filter-option-${opt?.toString()?.replaceAll('.', '')}`}
                  />
                  <ListItemText primary={opt} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasRBPProducts && (
            <>
              <FormControl
                id="groupname-filter-form"
                size="small"
                margin="normal"
                disabled={!groupNameFilterOptions.length}
                focused={isCalculating}
              >
                <InputLabel>Group Name</InputLabel>
                <Select
                  inputProps={{ id: 'groupname-filter-input-select' }}
                  multiple
                  label="Group Name"
                  placeholder="Group Name"
                  variant="outlined"
                  color="primary"
                  className="filter-select-input wide"
                  value={groupNameFilters}
                  onChange={({ target: { value } }) => setGroupNameFilters(value)}
                  renderValue={(selected) =>
                    selected.length === groupNameFilterOptions.length ? 'All' : selected.join(', ')
                  }
                >
                  {groupNameFilterOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      <Checkbox
                        color="primary"
                        checked={groupNameFilters.includes(opt)}
                        id={`filter-option-${opt?.toString()?.replaceAll('.', '')}`}
                      />
                      <ListItemText primary={opt} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                id="credit-filter-input-select"
                label="Credit Quality"
                className="filter-select-input wide"
                size="small"
                margin="normal"
                select
                focused={isCalculating}
                value={creditFilter}
                onChange={(e) => setCreditFilter(e.target.value)}
              >
                {Object.keys(creditFilterOptions).map((k, i) => (
                  <MenuItem key={i} value={k} id={`qa-option-${k}`}>
                    {creditFilterOptions[k]}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
        </div>
        {!isCalculating && (
          <Tooltip title={necessaryFieldsFilled ? '' : 'Must fill out all required fields'}>
            <div className="calculate-button-container">
              <BeamButton
                id="calculate-button"
                className="calculate-button"
                color="primary"
                size="medium"
                shape="square"
                onClick={triggerCalculations}
                disabled={!necessaryFieldsFilled}
                useInternalProcessing
              >
                Calculate
              </BeamButton>
            </div>
          </Tooltip>
        )}
      </div>
      <div className="datagrid-container">
        <DataGridPro
          className="base-datagrid"
          components={{ NoRowsOverlay: NoRows }}
          componentsProps={{
            noRowsOverlay: {
              text: isCalculating
                ? 'No products matching filters'
                : 'Enter a state and loan amount to calculate monthly payments',
              isProcessing,
            },
          }}
          columns={columns}
          rows={filteredRows}
          density="compact"
          disableSelectionOnClick
          disableColumnResize
          disableColumnReorder
          disableColumnMenu
          hideFooter
          initialState={{ sorting: { sortModel: [{ field: 'monthlyPayment', sort: 'asc' }] } }}
          getRowClassName={(params) => `product-compare-table-row--${params.row.term}-${params.row.apr}`}
        />
      </div>
      <div>
        <Footnotes />
      </div>
    </div>
  );
};

const ComparisonPageWithLicenses = withDataLoaded({
  WithData: ProductComparisonPage,
  WithoutData: PageSpinner,
  data: [userLicenseListLoader],
});

export default ComparisonPageWithLicenses;
